import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import KpiCard from '../../components/common/KpiCard';
import GraficoEventosDia from '../../components/common/GraficoEventosDia';
import BarrasRanking from '../../components/common/BarrasRanking';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import { useAuth } from '../../contexts/AuthContext';
import * as estatisticasApi from '../../api/estatisticas';
import * as eventosApi from '../../api/eventos';
import { formatDataHora, tempoRelativo } from '../../utils/date';
import { PERIODOS } from '../../utils/webhookOpcoes';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dias, setDias] = useState(30);
  const [resumo, setResumo] = useState(null);
  const [ultimos, setUltimos] = useState([]);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const [respResumo, respEventos] = await Promise.all([
        estatisticasApi.resumo(dias),
        eventosApi.listar({ limit: 8 }),
      ]);
      setResumo(respResumo.data);
      setUltimos(respEventos.data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar o dashboard.'));
    }
  }, [dias]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (erro && !resumo) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!resumo) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        titulo="Dashboard"
        subtitulo={`Volume recebido nos últimos ${dias} dias`}
        acoes={
          <>
            <select className="wh-select" style={{ width: 130 }} value={dias} onChange={(e) => setDias(Number(e.target.value))}>
              {PERIODOS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <button type="button" className="wh-btn wh-btn-outline" onClick={carregar}>
              <i className="bi bi-arrow-clockwise" /> Atualizar
            </button>
          </>
        }
      />

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <KpiCard icone="bi-inbox-fill" tom="accent" valor={resumo.total_periodo} label="Eventos no período" />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard icone="bi-clock-fill" valor={resumo.total_24h} label="Últimas 24h" />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard
            icone="bi-hdd-network-fill"
            valor={`${resumo.endpoints_ativos}/${resumo.total_endpoints}`}
            label="Endpoints ativos"
          />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard
            icone="bi-shield-exclamation"
            tom={resumo.credencial_invalida ? 'atencao' : 'neutro'}
            valor={resumo.credencial_invalida}
            label="Credencial inválida"
            hint="token/assinatura recusados"
          />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard
            icone="bi-x-octagon-fill"
            tom={resumo.rejeitados ? 'critico' : 'neutro'}
            valor={resumo.rejeitados}
            label="Rejeitados"
            hint="método, tamanho ou credencial"
          />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard
            icone="bi-files"
            valor={resumo.duplicados}
            label="Conteúdo repetido"
            hint="corpo idêntico a outro já recebido"
          />
        </div>
        {(user?.perfil === 'admin' || user?.perfil === 'operador') && (
          <div
            className="col-6 col-lg-3"
            role="button"
            onClick={() => navigate('/quarentena')}
            style={{ cursor: 'pointer' }}
          >
            <KpiCard
              icone="bi-question-octagon"
              tom={resumo.nao_identificadas ? 'atencao' : 'neutro'}
              valor={resumo.nao_identificadas ?? 0}
              label="Não identificadas"
              hint="slugs chamados que não existem"
            />
          </div>
        )}
      </div>

      <div className="card-soft mb-3">
        <div className="card-soft__header">
          <h2 className="card-soft__title">Eventos recebidos por dia</h2>
          <span style={{ fontSize: 12, color: 'var(--wh-muted)' }}>{resumo.total_periodo} no período</span>
        </div>
        <div className="card-soft__body">
          <GraficoEventosDia dados={resumo.por_dia} />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-lg-6">
          <div className="card-soft h-100">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Endpoints que mais receberam</h2>
            </div>
            <div className="card-soft__body">
              <BarrasRanking
                itens={resumo.por_endpoint.map((e) => ({ chave: e.id_endpoint, rotulo: e.nome, total: e.total, id: e.id_endpoint }))}
                rotuloVazio="Nenhum endpoint recebeu evento no período."
                onClickItem={(item) => navigate(`/eventos?id_endpoint=${item.id}`)}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="card-soft h-100">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Sistemas de origem detectados</h2>
            </div>
            <div className="card-soft__body">
              <BarrasRanking
                itens={resumo.por_sistema.map((s) => ({ chave: s.sistema, rotulo: s.sistema, total: s.total, id: s.sistema }))}
                rotuloVazio="Nenhuma origem identificada no período."
                onClickItem={(item) =>
                  item.id !== 'Não identificado' ? navigate(`/eventos?sistema=${encodeURIComponent(item.id)}`) : null
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card-soft">
        <div className="card-soft__header">
          <h2 className="card-soft__title">Últimos eventos</h2>
          <button type="button" className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => navigate('/eventos')}>
            Ver todos <i className="bi bi-arrow-right" />
          </button>
        </div>
        {ultimos.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox" />
            <div>Nenhum evento recebido ainda.</div>
          </div>
        ) : (
          <div className="wh-table-wrap">
            <table className="wh-table">
              <thead>
                <tr>
                  <th>Recebido</th>
                  <th>Origem</th>
                  <th>Tipo</th>
                  <th>Método</th>
                  <th>Credencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {ultimos.map((ev) => (
                  <tr key={ev.id_evento} className="is-clicavel" onClick={() => navigate(`/eventos/${ev.id_evento}`)}>
                    <td title={formatDataHora(ev.recebido_em)}>{tempoRelativo(ev.recebido_em)}</td>
                    <td>{ev.sistema_detectado || <span style={{ color: 'var(--wh-muted)' }}>não identificado</span>}</td>
                    <td className="wh-mono">{ev.evento_tipo || '—'}</td>
                    <td>
                      <Pill tone="neutral">{ev.metodo}</Pill>
                    </td>
                    <td>
                      <StatusBadge status={ev.auth_resultado} />
                    </td>
                    <td>
                      <StatusBadge status={ev.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
